import { EMPTY_ARRAY } from "@multiweb/reactUtils/constants";
import { IMenuItemData, IOpenedFormHistoryInfo, MenuItemType, NotificationItemId } from "@multiweb/types/contracts";
import { IAppState } from "@multiweb/types/react";
import { isFormItemInstanceFavoriteActionEnabled } from "../favoriteItems";
import { FavoriteMenuItem } from "../favoriteItems/types";
import { LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS } from "../locKeys";
import { logger } from "../logger";
import { getOpenedFormInfo } from "../openedItems";
import { SearchResult, SearchResultType } from "../search";
import {
    areFormItemWindowActionsEnabled,
    areProfileWindowActionsEnabled,
    createGetProfileIdsForForm,
    getFormItem,
    getPanelItem,
    isFormItemInstanceCloseActionEnabled,
} from "../selectors";
import { createItemIdForProfile } from "../utils";
import { MenuFocusableItemType, MenuNavigationSectionType, MutableNavigationNode, NavigationNode, NavigationSection } from "./types";
import {
    connectHorizontally,
    connectMultipleHorizontally,
    connectMultipleToEsc,
    connectMultipleToLeftNode,
    connectMultipleVerticallyCyclic,
    createMultipleNavigationNodes,
    createNavigationNode,
    searchGroupToNavigationSection,
} from "./utils";

/**
 * Vytvoří navigační uzly pro kontextové menu formulářové položky menu. Navigační sekce, do které vytvořené navigační
 * uzly spadají, je převzata ze zadaného navigačního uzlu, pro který se vytváří.
 * @param formItemNavigationNode Navigační uzel rodičovské formulářové položky.
 * @param windowActionsEnabled Příznak, zda jsou povolené akce pro otvírání nových instancí formuláře.
 * @param openActionEnabled Příznak, zda je povolené zobrazení kontextové akce pro otevření.
 *
 */
export const createFormItemActionNavigationNodes = (formItemNavigationNode: MutableNavigationNode, windowActionsEnabled: boolean, openActionEnabled: boolean): MutableNavigationNode[] => {
    const formItemId = formItemNavigationNode.self.itemId;
    const section = formItemNavigationNode.self.section;
    const favoriteActionNode = createNavigationNode(formItemId, MenuFocusableItemType.favoriteActionItem, section);
    const actionNodes = [favoriteActionNode];
    if (windowActionsEnabled) {
        if (openActionEnabled) {
            const openActionNode = createNavigationNode(formItemId, MenuFocusableItemType.openActionItem, section);
            actionNodes.push(openActionNode);
        }
        const openInTabActionNode = createNavigationNode(formItemId, MenuFocusableItemType.openInTabActionItem, section);
        actionNodes.push(openInTabActionNode);
        connectMultipleHorizontally(actionNodes);
    }
    connectHorizontally(formItemNavigationNode, favoriteActionNode);
    connectMultipleToEsc(actionNodes, formItemNavigationNode);
    return actionNodes;
};

/**
 * Vytvoří navigační uzly pro kontextové menu profilové položky menu. Navigační sekce, do které vytvořené navigační
 * uzly spadají, je převzata ze zadaného navigačního uzlu, pro který se vytváří.
 * @param profileNavigationNode Navigační uzel rodičovské profilové položky.
 * @param windowActionsEnabled Indikátor, zda jsou povolené akce pro otvírání nových instancí formuláře.
 * @param openActionEnabled Příznak, zda je povolené zobrazení kontextové akce pro otevření.
 */
export const createProfileActionNavigationNodes = (profileNavigationNode: MutableNavigationNode, windowActionsEnabled: boolean, openActionEnabled: boolean): MutableNavigationNode[] => {
    const profileId = profileNavigationNode.self.itemId;
    const section = profileNavigationNode.self.section;
    if (windowActionsEnabled) {
        const actionNodes = [];
        if (openActionEnabled) {
            const openActionNode = createNavigationNode(profileId, MenuFocusableItemType.openActionItem, section);
            actionNodes.push(openActionNode);
        }
        const openInTabActionNode = createNavigationNode(profileId, MenuFocusableItemType.openInTabActionItem, section);
        actionNodes.push(openInTabActionNode);
        connectMultipleHorizontally(actionNodes);
        connectHorizontally(profileNavigationNode, actionNodes[0]);
        connectMultipleToEsc(actionNodes, profileNavigationNode);
        return actionNodes;
    } else {
        return EMPTY_ARRAY as MutableNavigationNode[];
    }
};

/**
 * Vytvoří navigační uzly pro kontextové menu otevřeného formuláře bez položky v menu. Navigační sekce, do které vytvořené navigační
 * uzly spadají, je převzata ze zadaného navigačního uzlu, pro který se vytváří.
 * @param formInstanceNavigationNode Navigační uzel rodičovské položky otevřeného formuláře.
 * @param isFavoriteActionEnabled Příznak, zda je povolená akce pro oblíbené.
 * @param isCloseActionEnabled Příznak, zda je povolená akce pro zavření.
 */
export const createOpenedFormActionNavigationNodes = (formInstanceNavigationNode: MutableNavigationNode, isFavoriteActionEnabled: boolean, isCloseActionEnabled: boolean): MutableNavigationNode[] => {
    const section = formInstanceNavigationNode.self.section;
    const actionNodes = isFavoriteActionEnabled ? [createNavigationNode(formInstanceNavigationNode.self.itemId, MenuFocusableItemType.favoriteActionItem, section)] : [];
    return createAndConnectSharedActionItemNavigationNodes(actionNodes, formInstanceNavigationNode, true, isCloseActionEnabled);
};

/**
 * Vytvoří navigační uzly pro kontextové menu otevřeného formuláře. Navigační sekce, do které vytvořené navigační
 * uzly spadají, je převzata ze zadaného navigačního uzlu, pro který se vytváří.
 * @param formInstanceNavigationNode Navigační uzel rodičovské položky otevřeného formuláře.
 * @param windowActionsEnabled Indikátor, zda jsou povolené akce pro otvírání nových instancí formuláře.
 * @param isCloseActionEnabled Indikátor, zda je povolená akce pro zavření instance.
 */
export const createOpenedFormItemActionNavigationNodes = (formInstanceNavigationNode: MutableNavigationNode, windowActionsEnabled: boolean, isCloseActionEnabled: boolean): MutableNavigationNode[] => {
    const section = formInstanceNavigationNode.self.section;
    const favoriteActionNode = createNavigationNode(formInstanceNavigationNode.self.itemId, MenuFocusableItemType.favoriteActionItem, section);
    return createAndConnectSharedActionItemNavigationNodes([favoriteActionNode], formInstanceNavigationNode, windowActionsEnabled, isCloseActionEnabled);
};

/**
 * Vytvoří navigační uzly pro kontextové menu otevřeného profilu. Navigační sekce, do které vytvořené navigační
 * uzly spadají, je převzata ze zadaného navigačního uzlu, pro který se vytváří.
 * @param formInstanceNavigationNode Navigační uzel rodičovské položky otevřeného formuláře.
 * @param windowActionsEnabled Indikátor, zda jsou povolené akce pro otvírání nových instancí formuláře.
 * @param isCloseActionEnabled Indikátor, zda je povolená akce pro zavření instance.
 */
export const createOpenedProfileActionNavigationNodes = (formInstanceNavigationNode: MutableNavigationNode, windowActionsEnabled: boolean, isCloseActionEnabled: boolean) => (
    createAndConnectSharedActionItemNavigationNodes([], formInstanceNavigationNode, windowActionsEnabled, isCloseActionEnabled)
);

/**
 * Obsahuje společný kód pro tvorbu akčních položek pro otevřené formulářové položky a profilové položky. Vytváří položky pro otevření, pokud je otvírání povoleno.
 * Vytváří položku pro zavření instance. Spojí vstupní a nově vytvořené položky horizontálně. Spojí vstupní a nově vytvořené položky na esc klávesu na rodičovský
 * uzel. Propojí první akční položku horizontálně s rodičovskou položkou.
 * @param actionNodes Vstupní uzly akčních položek. Pro položku formuláře obsahuje akční položku přidat/odebrat do/z oblíbených. Pro profily prázdné pole.
 * @param parentNode Rodičovský navigační uzel položky otevřeného formuláře/profilu.
 * @param windowActionsEnabled Indikátor, zda jsou povolené akce pro otvírání nových instancí formuláře.
 * @param isCloseActionEnabled Indikátor, zda je povolená akce pro zavření instance.
 */
const createAndConnectSharedActionItemNavigationNodes = (actionNodes: MutableNavigationNode[], parentNode: MutableNavigationNode, windowActionsEnabled: boolean, isCloseActionEnabled: boolean) => {
    const formInstanceId = parentNode.self.itemId;
    const section = parentNode.self.section;
    if (windowActionsEnabled) {
        const openActionNode = createNavigationNode(formInstanceId, MenuFocusableItemType.openActionItem, section);
        const openInTabActionNode = createNavigationNode(formInstanceId, MenuFocusableItemType.openInTabActionItem, section);
        actionNodes.push(openActionNode, openInTabActionNode);
    }
    if (isCloseActionEnabled) {
        const closeActionNode = createNavigationNode(formInstanceId, MenuFocusableItemType.closeActionItem, section);
        actionNodes.push(closeActionNode);
    }
    if (actionNodes.length > 0) {
        connectMultipleHorizontally(actionNodes);
        connectMultipleToEsc(actionNodes, parentNode);
        connectHorizontally(parentNode, actionNodes[0]);
    }
    return actionNodes;
};

/**
 * Rekurzivně vytvoří navigační uzly pro položky submenu a všechny další zanořené položky.
 * Vytváří uzly pro navigační sekci formulářů.
 *
 * @param folderMenuItemId Id položky submenu.
 * @param submenuItemIds Identifikátory potomků submenu v první úrovni.
 * @param state Stav, ze kterého se získávají data o položkách.
 */
export const createSubmenuNodes = (folderMenuItemId: number, submenuItemIds: readonly number[] | undefined, state: IAppState) => {
    const folderNode = createNavigationNode(folderMenuItemId, MenuFocusableItemType.folderItem, MenuNavigationSectionType.forms);
    const submenuNodes: MutableNavigationNode[] = [];
    const actionItemNodes: MutableNavigationNode[] = [];
    const nestedNodes: MutableNavigationNode[] = [];

    if (!submenuItemIds || submenuItemIds.length === 0) {
        return { folderNode, submenuNodes, actionItemNodes, nestedNodes };
    }

    for (const submenuItemId of submenuItemIds) {
        const submenuItem = getFormItem(state, submenuItemId);
        if (!submenuItem) {
            logger.error("Cannot find submenu item with id: %s", submenuItemId);
            continue;
        }

        if (submenuItem.type === MenuItemType.form) {
            const formItemNodes = createFormItemNavigationNodesWithProfiles(submenuItem, state, MenuNavigationSectionType.forms, true);
            submenuNodes.push(...formItemNodes.verticalNodes);
            actionItemNodes.push(...formItemNodes.actionItemNodes);
        } else if (submenuItem.type === MenuItemType.folder) {
            const nestedSubmenu = createSubmenuNodes(submenuItem.id, submenuItem.children, state);
            submenuNodes.push(nestedSubmenu.folderNode);
            actionItemNodes.push(...nestedSubmenu.actionItemNodes);
            nestedNodes.push(...nestedSubmenu.submenuNodes, ...nestedSubmenu.nestedNodes);
        } else {
            logger.error("Cannot create navigation node for unknown menu item type: %s", submenuItem.type);
        }
    }

    connectChildrenAndParent(folderNode, submenuNodes);

    return { folderNode, submenuNodes, actionItemNodes, nestedNodes };
};

/**
 * Vytvoří navigační uzly pro položku formuláře, včetně jeho akčních položek. Nevytváří uzly pro
 * profily dané položky formuláře, k tomu slouží funkce:
 * @see createFormItemNavigationNodesWithProfiles
 *
 * @param formItemId Id položky formuláře, pro kterou se vytváří navigační uzly.
 * @param state Stav pro získání restrikcí pro otevírání nových instancí formuláře.
 * @param section Sekce, pro kterou se navigační uzly vytváří.
 * @param openActionEnabled Příznak, zda je povolené zobrazení kontextové akce pro otevření.
 */
export const createFormItemNavigationNodes = (formItemId: number, state: IAppState, section: NavigationSection, openActionEnabled: boolean) => {
    const formItemNode = createNavigationNode(formItemId, MenuFocusableItemType.formItem, section);
    const areWindowActionsEnabled = areFormItemWindowActionsEnabled(state, formItemId);
    const formItemActionNodes: MutableNavigationNode[] = createFormItemActionNavigationNodes(formItemNode, areWindowActionsEnabled, openActionEnabled);
    return { formItemNode, formItemActionNodes };
};

/**
 * Vytvoří navigační uzly pro položku formuláře, včetně jeho akčních položek, napojených profilů a jejich
 * akčních položek.
 *
 * @param formItem Položka formuláře, pro kterou se vytváří navigační uzly.
 * @param state Stav pro získání restrikcí pro otevírání nových instancí formuláře.
 * @param section Sekce, pro kterou se navigační uzly vytváří.
 * @param openActionEnabled Příznak, zda je povolené zobrazení kontextové akce pro otevření.
 */
export const createFormItemNavigationNodesWithProfiles = (formItem: IMenuItemData, state: IAppState, section: NavigationSection, openActionEnabled: boolean) => {
    const { formItemNode, formItemActionNodes } = createFormItemNavigationNodes(formItem.id, state, section, openActionEnabled);
    const verticalNodes = [formItemNode];
    const actionItemNodes = [...formItemActionNodes];
    const getProfileIdsForForm = createGetProfileIdsForForm();

    if (formItem.profiles) {
        const profileIds = getProfileIdsForForm(state, formItem.id);
        for (const profileId of profileIds) {
            const { profileNode, profileActionNodes } = createProfileNavigationNodes(profileId, state, section, openActionEnabled, formItem.id);
            verticalNodes.push(profileNode);
            actionItemNodes.push(...profileActionNodes);
        }
    }

    return { verticalNodes, actionItemNodes };
};

/**
 * Vytvoří navigační uzly pro položku profilu, včetně jeho akčních položek.
 * @param profileId Id profilu, pro který se vytváří navigační uzly.
 * @param state Stav pro získání restrikcí pro otevírání nových instancí formuláře.
 * @param section Sekce, pro kterou se navigační uzly vytváří.
 * @param parentFormItemId Id formulářové položky, pod kterou je profil vykreslený.
 * @param openActionEnabled Příznak, zda je povolené zobrazení kontextové akce pro otevření.
 */
export const createProfileNavigationNodes = (profileId: number, state: IAppState, section: NavigationSection, openActionEnabled: boolean, parentFormItemId?: number) => {
    const itemId = createItemIdForProfile(profileId, parentFormItemId);
    const profileNode = createNavigationNode(itemId, MenuFocusableItemType.profile, section);
    const areWindowActionsEnabled = areProfileWindowActionsEnabled(state, profileId);
    const profileActionNodes = createProfileActionNavigationNodes(profileNode, areWindowActionsEnabled, openActionEnabled);
    return { profileNode, profileActionNodes };
};

/**
 * Propojí rodičovský navigační uzel s jeho potomky následujícím způsobem:
 * 1. Vertikálně propojí všechny potomky.
 * 2. Propojí rodičovský uzel jako levého souseda pro všechny potomky.
 * 3. Propojí rodičovský uzel jako esc pro všechny potomky.
 * 4. Propojí prvního potomka jako pravého souseda rodičovského uzlu.
 *
 * @param parentNode Rodičovský navigační uzel.
 * @param childNodes Pole navigačních uzlů potomků.
 */
export const connectChildrenAndParent = (parentNode: MutableNavigationNode, childNodes: readonly MutableNavigationNode[]) => {
    connectMultipleVerticallyCyclic(childNodes);
    connectMultipleToLeftNode(childNodes, parentNode);
    connectMultipleToEsc(childNodes, parentNode);
    if (childNodes.length > 0) {
        parentNode.rightId = childNodes[0].self.id;
    }
};

/**
 * Vytvoří navigační uzly pro otevřené položky v sekci Rychlý přístup (bez aktivního vyhledávání), včetně akcí v jejich kontextové nabídce.
 *
 * @param openedItemIds Identifikátory otevřených položek, pro které se generují navigační uzly.
 * @param section Sekce, pro kterou se navigační uzly vytváří.
 * @param state Stav pro získání restrikcí pro otevírání nových instancí formuláře.
 */
export const createOpenedItemNavigationNodes = (openedItemIds: readonly string[], section: NavigationSection, state: IAppState) => {
    const verticalNodes: MutableNavigationNode[] = [];
    const actionItemNodes: MutableNavigationNode[] = [];
    for (const openedItemId of openedItemIds) {
        const openedFormInfo = getOpenedFormInfo(state, openedItemId);
        if (!openedFormInfo) {
            logger.warn("Navigation node generation failed. Cannot get opened form info for form instance %s", openedItemId);
            continue;
        }
        const { formInstanceId, profileId, menuItemId } = openedFormInfo;
        const openedItemNode = createNavigationNode(formInstanceId, MenuFocusableItemType.openedItem, section);
        const isCloseActionEnabled = isFormItemInstanceCloseActionEnabled(state, formInstanceId);
        let openedItemActionItemNodes;
        if (profileId && menuItemId) { // otevřená položka profilu
            const areWindowActionsEnabled = areProfileWindowActionsEnabled(state, profileId);
            openedItemActionItemNodes = createOpenedProfileActionNavigationNodes(openedItemNode, areWindowActionsEnabled, isCloseActionEnabled);
        } else if (menuItemId) { // otevřená položka formuláře
            const areWindowActionsEnabled = areFormItemWindowActionsEnabled(state, menuItemId);
            openedItemActionItemNodes = createOpenedFormItemActionNavigationNodes(openedItemNode, areWindowActionsEnabled, isCloseActionEnabled);
        } else { // otevřený formulář bez položky v menu
            const isFavoriteActionEnabled = isFormItemInstanceFavoriteActionEnabled(state, formInstanceId);
            openedItemActionItemNodes = createOpenedFormActionNavigationNodes(openedItemNode, isFavoriteActionEnabled, isCloseActionEnabled);
        }
        verticalNodes.push(openedItemNode);
        actionItemNodes.push(...openedItemActionItemNodes);
    }
    return { verticalNodes, actionItemNodes };
};

/**
 * Vytvoří navigační uzly pro historické položky v sekci Rychlý přístup (bez aktivního vyhledávání), včetně akcí v jejich kontextové nabídce.
 *
 * @param historyItems Historické položky, pro které se generují navigační uzly.
 * @param state Stav pro získání restrikcí pro otevírání nových instancí formuláře.
 */
export const createHistoryItemNavigationNodes = (historyItems: readonly IOpenedFormHistoryInfo[], state: IAppState) => {
    const verticalNodes: MutableNavigationNode[] = [];
    const actionItemNodes: MutableNavigationNode[] = [];
    for (const { profileId, menuItemId } of historyItems) {
        const id = profileId ? createItemIdForProfile(profileId) : menuItemId;
        const type = profileId ? MenuFocusableItemType.profile : MenuFocusableItemType.formItem;
        const historyItemNode = createNavigationNode(id, type, MenuNavigationSectionType.quickAccessHistoric);
        verticalNodes.push(historyItemNode);
        if (profileId) {
            const areWindowActionsEnabled = areProfileWindowActionsEnabled(state, profileId);
            const profileActionNodes = createProfileActionNavigationNodes(historyItemNode, areWindowActionsEnabled, false);
            actionItemNodes.push(...profileActionNodes);
        } else {
            const areWindowActionsEnabled = areFormItemWindowActionsEnabled(state, menuItemId);
            const formItemActionNodes: MutableNavigationNode[] = createFormItemActionNavigationNodes(historyItemNode, areWindowActionsEnabled, false);
            actionItemNodes.push(...formItemActionNodes);
        }
    }
    return { verticalNodes, actionItemNodes };
};

/**
 * Vytvoří navigační uzly pro sekci Rychlý přístup a provede jejich propojení.
 * @param openedItemIds Položky otevřených formulářů.
 * @param historyItems Položky historicky otevřených formulářů.
 * @param favoriteItemIds Identifikátory položek oblíbených formulářů s profily.
 * @param quickAccessSectionNode Navigační uzel sekce Rychlý přístup, ke kterému se napojí vytvořené uzly.
 * @param searchInputNode Navigační uzel pole pro vyhledávání.
 * @param state Stav pro získání omezení pro otvírání nových instancí formulářů.
 */
export const createQuickAccessMenuNavigationNodes = (
    openedItemIds: readonly string[],
    historyItems: readonly IOpenedFormHistoryInfo[],
    favoriteItemIds: readonly FavoriteMenuItem[],
    quickAccessSectionNode: NavigationNode,
    searchInputNode: MutableNavigationNode,
    state: IAppState,
) => {
    const openedItemNodes = createOpenedItemNavigationNodes(openedItemIds, MenuNavigationSectionType.quickAccessOpened, state);
    const historyItemNodes = createHistoryItemNavigationNodes(historyItems, state);
    const favoriteItemsNodes = createFavoriteMenuNavigationNodes(favoriteItemIds, state);

    const verticalNodes: MutableNavigationNode[] = [
        ...openedItemNodes.verticalNodes,
        ...historyItemNodes.verticalNodes,
        ...favoriteItemsNodes.verticalNodes,
    ];
    connectMultipleVerticallyCyclic([searchInputNode, ...verticalNodes]);
    connectMultipleToLeftNode(verticalNodes, quickAccessSectionNode);
    connectMultipleToEsc(verticalNodes, quickAccessSectionNode);

    return [...verticalNodes, ...openedItemNodes.actionItemNodes, ...historyItemNodes.actionItemNodes, ...favoriteItemsNodes.actionItemNodes];
};

/**
 * Vytvoří navigační uzly pro oblíbené formuláře v sekci Rychlý přístup (bez aktivního vyhledávání).
 * @param favoriteFormIds Identifikátory oblíbených položek formulářů.
 * @param state Stav pro získání omezení pro otvírání nových instancí formulářů.
 */
export const createFavoriteMenuNavigationNodes = (favoriteFormIds: readonly FavoriteMenuItem[], state: IAppState) => {
    const actionItemNodes: NavigationNode[] = [];
    const verticalNodes: NavigationNode[] = [];
    for (const favoriteForm of favoriteFormIds) {
        const formItem = getFormItem(state, favoriteForm.formId);
        if (!formItem) {
            logger.error("Cannot find favorite form item with id: %s. Skipping creation of keyboard navigation node.", favoriteForm.formId);
            continue;
        }

        const formItemNodes = createFormItemNavigationNodesWithProfiles(formItem, state, MenuNavigationSectionType.quickAccessFavorite, true);
        verticalNodes.push(...formItemNodes.verticalNodes);
        actionItemNodes.push(...formItemNodes.actionItemNodes);
    }
    return { verticalNodes, actionItemNodes };
};

/**
 * Vytvoří navigační uzly pro formulářové položky, které nejsou kořenové. Provede jejich propojení na kořenové položky.
 * @param rootFormNodes Navigační uzly kořenových formulářových položek, které se zobrazují v panelu menu.
 * @param state Stav pro získání omezení pro otvírání nových instancí formulářů.
 */
export const createFormMenuNavigationNodes = (rootFormNodes: MutableNavigationNode[], state: IAppState) => {
    return rootFormNodes.reduce((result, rootFormNode) => {
        const panelItem = getPanelItem(state, rootFormNode.self.itemId as string);
        const formItemId = panelItem?.itemId as number | undefined;
        if (!formItemId) {
            return result;
        }
        const formItem = getFormItem(state, formItemId);
        if (formItem?.type === MenuItemType.folder || formItem?.type === MenuItemType.combined) {
            const submenuNodes = createSubmenuNodes(formItemId, formItem.children, state);
            rootFormNode.rightId = submenuNodes.submenuNodes.length > 0 ? submenuNodes.submenuNodes[0].self.id : undefined;
            connectMultipleToLeftNode(submenuNodes.submenuNodes, rootFormNode);
            connectMultipleToEsc(submenuNodes.submenuNodes, rootFormNode);
            return [
                ...result,
                ...submenuNodes.submenuNodes,
                ...submenuNodes.actionItemNodes,
                ...submenuNodes.nestedNodes,
            ];
        } else {
            return result;
        }
    }, [] as MutableNavigationNode[]);
};

/**
 * Vytvoří navigační uzly pro výsledky vyhledávání a provede jejich propojení.
 * @param searchResults Výsledky vyhledávání.
 * @param quickAccessSectionNode Navigační uzel sekce Rychlý přístup.
 * @param searchInputNode Navigační uzel vyhledávacího pole, ke kterému se napojí vytvořené uzly.
 * @param state Stav pro získání omezení pro otvírání nových instancí formulářů.
 */
export const createSearchResultNavigationNodes = (searchResults: readonly SearchResult[], quickAccessSectionNode: NavigationNode, searchInputNode: MutableNavigationNode, state: IAppState): readonly NavigationNode[] => {
    if (searchResults.length === 0) {
        searchInputNode.upId = undefined;
        searchInputNode.downId = undefined;
        return EMPTY_ARRAY as readonly NavigationNode[];
    }
    const actionItemNodes: NavigationNode[] = [];
    const verticalNodes: NavigationNode[] = [];

    for (const { id, type, searchGroup } of searchResults) {
        const openActionEnabled = searchGroup !== LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS;
        const section = searchGroupToNavigationSection(searchGroup);
        if (type === SearchResultType.form) {
            const { formItemNode, formItemActionNodes } = createFormItemNavigationNodes(id as number, state, section, openActionEnabled);
            verticalNodes.push(formItemNode);
            actionItemNodes.push(...formItemActionNodes);
        } else if (type === SearchResultType.profile) {
            const { profileNode, profileActionNodes } = createProfileNavigationNodes(id as number, state, section, openActionEnabled);
            verticalNodes.push(profileNode);
            actionItemNodes.push(...profileActionNodes);
        } else if (type === SearchResultType.formInstance) {
            const openedItemNodes = createOpenedItemNavigationNodes([id as string], MenuNavigationSectionType.searchResultsOpened, state);
            verticalNodes.push(...openedItemNodes.verticalNodes);
            actionItemNodes.push(...openedItemNodes.actionItemNodes);
        } else if (type === SearchResultType.groupHeader) {
            continue; // Nadpisy vyhledávacích skupin nejsou navigovatelné.
        } else {
            logger.error("Unknown type of search result: %s", type);
        }
    }

    connectMultipleVerticallyCyclic([searchInputNode, ...verticalNodes]);
    connectMultipleToLeftNode(verticalNodes, quickAccessSectionNode);
    connectMultipleToEsc(verticalNodes, quickAccessSectionNode);

    return verticalNodes.concat(actionItemNodes);
};

/**
 * Vytvoří navigační uzly pro menu notifikací a propojí je.
 */
export const createNotificationMenuNavigationNodes = (notificationItemIds: readonly NotificationItemId[], notificationGroupNode: MutableNavigationNode) => {
    const notificationNodes = createMultipleNavigationNodes(notificationItemIds, MenuFocusableItemType.notificationItem, MenuNavigationSectionType.notifications);
    connectChildrenAndParent(notificationGroupNode, notificationNodes);
    return notificationNodes;
};
